import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search,
  ChevronDown,
  ChevronUp,
  X,
  Building2,
  MoreHorizontal,
  Plus,
  Download,
  SlidersHorizontal,
  ArrowUpDown,
  MapPin,
  User,
  DollarSign,
  Mail,
  Phone,
  Clock
} from 'lucide-react';
import CreateOrganizationModal from '@/components/CreateOrganizationModal';
import ViewOrganizationModal from '@/components/ViewOrganizationModal';
import { useToastNotification } from '@/contexts/ToastContext';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    prospect: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    inactive: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Type badge component
const TypeBadge = ({ type }) => {
  const typeStyles = {
    lp: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    gp: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    portfolio: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    advisor: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
  };

  const typeLabels = {
    lp: 'LP',
    gp: 'GP',
    portfolio: 'Portfolio Co.',
    advisor: 'Advisor'
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${typeStyles[type]}`}>
      {typeLabels[type]}
    </span>
  );
};

const Organizations = () => {
  const toast = useToastNotification();
  const navigate = useNavigate();
  
  // State for organizations data
  const [organizations, setOrganizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [viewingOrganization, setViewingOrganization] = useState(null);
  
  // State for filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [aumFilter, setAumFilter] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Helper function to show toast (uses global context)
  const showToast = (type, message, addonText) => {
    toast[type === 'gray' ? 'gray' : type](message, addonText);
  };

  // Fetch organizations and contacts on mount
  useEffect(() => {
    fetchOrganizations();
    fetchContacts();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api/organizations`, {
        withCredentials: true
      });
      setOrganizations(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations');
      setOrganizations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contacts`, {
        withCredentials: true
      });
      setContacts(response.data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  };

  // Navigate to contacts page and highlight a specific contact
  const handlePrimaryContactClick = (primaryContactName) => {
    if (!primaryContactName) return;
    
    // Find the contact by name (first + last name match)
    const matchingContact = contacts.find(c => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      return fullName === primaryContactName.toLowerCase();
    });
    
    if (matchingContact) {
      navigate(`/contacts/all?highlight=${matchingContact.id}`);
    } else {
      // If no exact match, navigate to contacts with search
      navigate(`/contacts/all?search=${encodeURIComponent(primaryContactName)}`);
    }
  };

  // Handle updating last contact time to now (UTC)
  const handleUpdateLastContact = async (orgId) => {
    try {
      const nowUTC = new Date().toISOString();
      const response = await axios.patch(
        `${API_URL}/api/organizations/${orgId}`,
        { lastContact: nowUTC },
        { withCredentials: true }
      );
      setOrganizations(prev => prev.map(o => o.id === orgId ? response.data : o));
      showToast('success', 'Last contact updated', 'Success');
    } catch (err) {
      console.error('Error updating last contact:', err);
      showToast('error', 'Failed to update last contact', 'Error');
    }
  };

  // Handle clearing last contact time (Cmd/Ctrl+Click)
  const handleClearLastContact = async (orgId) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/organizations/${orgId}`,
        { lastContact: '' },
        { withCredentials: true }
      );
      setOrganizations(prev => prev.map(o => o.id === orgId ? response.data : o));
      showToast('gray', 'Last contact cleared', 'Info');
    } catch (err) {
      console.error('Error clearing last contact:', err);
      showToast('error', 'Failed to clear last contact', 'Error');
    }
  };

  const handleCreateOrganization = async (orgData, orgId) => {
    try {
      if (orgId) {
        // Update existing organization
        const response = await axios.patch(
          `${API_URL}/api/organizations/${orgId}`,
          orgData,
          { withCredentials: true }
        );
        setOrganizations(prev => prev.map(o => o.id === orgId ? response.data : o));
        
        // Update viewing organization if it's the same one being edited
        if (viewingOrganization && viewingOrganization.id === orgId) {
          setViewingOrganization(response.data);
        }
      } else {
        // Create new organization
        const response = await axios.post(
          `${API_URL}/api/organizations`,
          orgData,
          { withCredentials: true }
        );
        setOrganizations(prev => [...prev, response.data]);
      }
      setEditingOrganization(null);
    } catch (err) {
      console.error('Error saving organization:', err);
      throw err;
    }
  };

  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/organizations/${orgId}`, {
        withCredentials: true
      });
      setOrganizations(prev => prev.filter(o => o.id !== orgId));
      showToast('success', 'Organization deleted', 'Success');
    } catch (err) {
      console.error('Error deleting organization:', err);
      showToast('error', 'Failed to delete organization', 'Error');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedOrgs.length} organization(s)?`)) return;
    
    try {
      await axios.post(
        `${API_URL}/api/organizations/bulk-delete`,
        selectedOrgs,
        { withCredentials: true }
      );
      setOrganizations(prev => prev.filter(o => !selectedOrgs.includes(o.id)));
      setSelectedOrgs([]);
      showToast('success', `${selectedOrgs.length} organization(s) deleted`, 'Success');
    } catch (err) {
      console.error('Error deleting organizations:', err);
      showToast('error', 'Failed to delete organizations', 'Error');
    }
  };

  const handleEditOrganization = (org, editContext = 'organization') => {
    setEditingOrganization({ ...org, editContext });
    setIsEditModalOpen(true);
  };

  const handleViewOrganization = (org) => {
    setViewingOrganization(org);
    setIsViewModalOpen(true);
  };

  const handleAddNewOrganization = () => {
    setEditingOrganization(null);
    setIsEditModalOpen(true);
  };

  // Get unique values for filter options
  const locations = [...new Set(organizations.map(o => o.location).filter(Boolean))];
  const aumRanges = ['< $50M', '$50M - $500M', '$500M - $5B', '$5B - $50B', '$50B+', 'N/A'];

  // Format currency helper with smart abbreviations (K, M, B)
  const formatCurrency = (amount) => {
    if (!amount) return '';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return amount; // Return as-is if not a number (e.g., "$85B", "N/A")
    
    const absNum = Math.abs(num);
    
    // Billions
    if (absNum >= 1000000000) {
      const billions = num / 1000000000;
      return `$${billions % 1 === 0 ? billions.toFixed(0) : billions.toFixed(1)}B`;
    }
    
    // Millions
    if (absNum >= 1000000) {
      const millions = num / 1000000;
      // Show up to 3 decimals for precision, but remove trailing zeros
      const formatted = millions.toFixed(3).replace(/\.?0+$/, '');
      return `$${formatted}M`;
    }
    
    // Thousands
    if (absNum >= 1000) {
      const thousands = num / 1000;
      const formatted = thousands.toFixed(1).replace(/\.?0+$/, '');
      return `$${formatted}K`;
    }
    
    // Less than 1000, show as regular currency
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Helper to categorize AUM
  const getAumCategory = (aum) => {
    if (!aum || aum === 'N/A') return 'N/A';
    const value = parseFloat(aum.replace(/[$,BMK]/g, ''));
    const multiplier = aum.includes('B') ? 1000 : aum.includes('M') ? 1 : 0.001;
    const inMillions = value * multiplier;
    
    if (inMillions < 50) return '< $50M';
    if (inMillions < 500) return '$50M - $500M';
    if (inMillions < 5000) return '$500M - $5B';
    if (inMillions < 50000) return '$5B - $50B';
    return '$50B+';
  };

  // Filter and sort organizations
  const filteredOrgs = useMemo(() => {
    let result = [...organizations];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(org =>
        org.name.toLowerCase().includes(query) ||
        (org.primaryContact && org.primaryContact.toLowerCase().includes(query)) ||
        (org.location && org.location.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(org => org.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(org => org.type === typeFilter);
    }

    // Location filter
    if (locationFilter !== 'all') {
      result = result.filter(org => org.location === locationFilter);
    }

    // AUM filter
    if (aumFilter !== 'all') {
      result = result.filter(org => getAumCategory(org.aum) === aumFilter);
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [organizations, searchQuery, statusFilter, typeFilter, locationFilter, aumFilter, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedOrgs.length === filteredOrgs.length) {
      setSelectedOrgs([]);
    } else {
      setSelectedOrgs(filteredOrgs.map(o => o.id));
    }
  };

  // Handle individual select
  const handleSelectOrg = (id) => {
    setSelectedOrgs(prev =>
      prev.includes(id)
        ? prev.filter(oId => oId !== id)
        : [...prev, id]
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setLocationFilter('all');
    setAumFilter('all');
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || locationFilter !== 'all' || aumFilter !== 'all';

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="ml-1 text-zinc-600" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp size={14} className="ml-1 text-blue-400" />
      : <ChevronDown size={14} className="ml-1 text-blue-400" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-zinc-100">Organizations</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isLoading ? 'Loading...' : (
              <>
                {filteredOrgs.length} organization{filteredOrgs.length !== 1 ? 's' : ''}
                {hasActiveFilters && ` (filtered from ${organizations.length})`}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="bg-transparent border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
          >
            <Download size={16} />
            Export
          </Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleAddNewOrganization}
          >
            <Plus size={16} />
            Add Organization
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-[#0f1115] border border-white/6 rounded-lg p-4 space-y-4">
        {/* Basic Filters Row */}
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-[#0a0a0a] border-white/10 text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
            />
          </div>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[150px] bg-[#0a0a0a] border-white/10 text-zinc-300">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-[180px] bg-[#0a0a0a] border-white/10 text-zinc-300">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="lp">LPs</SelectItem>
              <SelectItem value="gp">GPs</SelectItem>
              <SelectItem value="portfolio">Portfolio Companies</SelectItem>
              <SelectItem value="advisor">Advisors</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced Filters Toggle */}
          <Button
            variant="outline"
            size="default"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`bg-transparent border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white ${
              showAdvancedFilters ? 'bg-white/5 text-white border-white/20' : ''
            }`}
          >
            <SlidersHorizontal size={16} />
            Advanced
            {showAdvancedFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>

        {/* Advanced Filters */}
        <AnimatePresence>
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="pt-3 border-t border-white/6 flex flex-col md:flex-row gap-3">
                {/* Location Filter */}
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="w-full md:w-[200px] bg-[#0a0a0a] border-white/10 text-zinc-300">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1115] border-white/10">
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* AUM Filter */}
                <Select value={aumFilter} onValueChange={setAumFilter}>
                  <SelectTrigger className="w-full md:w-[180px] bg-[#0a0a0a] border-white/10 text-zinc-300">
                    <SelectValue placeholder="AUM / Deal Size" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1115] border-white/10">
                    <SelectItem value="all">All AUM</SelectItem>
                    {aumRanges.map(range => (
                      <SelectItem key={range} value={range}>{range}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="default"
                    onClick={clearFilters}
                    className="text-zinc-400 hover:text-white hover:bg-white/5"
                  >
                    <X size={16} />
                    Clear Filters
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active Filters Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2">
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 text-blue-400 text-xs border border-blue-500/20">
                Search: "{searchQuery}"
                <button onClick={() => setSearchQuery('')} className="hover:text-blue-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-400 text-xs border border-emerald-500/20">
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('all')} className="hover:text-emerald-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {typeFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/10 text-purple-400 text-xs border border-purple-500/20">
                Type: {typeFilter === 'lp' ? 'LP' : typeFilter === 'gp' ? 'GP' : typeFilter === 'portfolio' ? 'Portfolio Co.' : 'Advisor'}
                <button onClick={() => setTypeFilter('all')} className="hover:text-purple-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {locationFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/10 text-sky-400 text-xs border border-sky-500/20">
                Location: {locationFilter}
                <button onClick={() => setLocationFilter('all')} className="hover:text-sky-300">
                  <X size={12} />
                </button>
              </span>
            )}
            {aumFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/10 text-amber-400 text-xs border border-amber-500/20">
                AUM: {aumFilter}
                <button onClick={() => setAumFilter('all')} className="hover:text-amber-300">
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      <AnimatePresence>
        {selectedOrgs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-3 flex items-center justify-between"
          >
            <span className="text-sm text-blue-400">
              {selectedOrgs.length} organization{selectedOrgs.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                Export Selected
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={handleBulkDelete}
              >
                Delete Selected
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedOrgs([])}
                className="text-zinc-400 hover:text-white"
              >
                Clear Selection
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="bg-[#0f1115] border border-white/6 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/6 hover:bg-transparent">
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={selectedOrgs.length === filteredOrgs.length && filteredOrgs.length > 0}
                  onCheckedChange={handleSelectAll}
                  className="border-white/20 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Organization
                  <SortIndicator columnKey="name" />
                </div>
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden md:table-cell"
                onClick={() => handleSort('type')}
              >
                <div className="flex items-center">
                  Type
                  <SortIndicator columnKey="type" />
                </div>
              </TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden md:table-cell"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center">
                  Status
                  <SortIndicator columnKey="status" />
                </div>
              </TableHead>
              <TableHead className="text-zinc-400 font-medium hidden lg:table-cell">Primary Contact</TableHead>
              <TableHead className="text-zinc-400 font-medium hidden lg:table-cell">Contact</TableHead>
              <TableHead className="text-zinc-400 font-medium hidden xl:table-cell">Location</TableHead>
              <TableHead className="text-zinc-400 font-medium hidden xl:table-cell">Deal Size (USD)</TableHead>
              <TableHead className="text-zinc-400 font-medium hidden xl:table-cell">Tags</TableHead>
              <TableHead 
                className="text-zinc-400 font-medium cursor-pointer hover:text-zinc-200 transition-colors hidden lg:table-cell"
                onClick={() => handleSort('lastContact')}
              >
                <div className="flex items-center">
                  Last Contact
                  <SortIndicator columnKey="lastContact" />
                </div>
              </TableHead>
              <TableHead className="text-zinc-400 font-medium w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="h-32 text-center">
                  <div className="flex flex-col items-center justify-center text-zinc-500">
                    <Building2 size={32} className="mb-2 opacity-50" />
                    <p className="text-sm">No organizations found</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-blue-400 hover:text-blue-300 text-sm mt-1"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrgs.map((org) => (
                <TableRow
                  key={org.id}
                  className={`border-white/6 transition-colors ${
                    selectedOrgs.includes(org.id)
                      ? 'bg-blue-500/5'
                      : 'hover:bg-white/[0.02]'
                  }`}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedOrgs.includes(org.id)}
                      onCheckedChange={() => handleSelectOrg(org.id)}
                      className="border-white/20 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <p className="font-medium text-zinc-200">{org.name}</p>
                      {org.website && (
                        <a
                          href={org.website.startsWith('http') ? org.website : `https://${org.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300 mt-0.5 transition-colors truncate max-w-[180px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {org.website}
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <TypeBadge type={org.type} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <StatusBadge status={org.status} />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {org.primaryContact ? (
                      <button
                        onClick={() => handlePrimaryContactClick(org.primaryContact)}
                        className="flex items-center gap-2 group text-left hover:bg-white/5 -m-1.5 p-1.5 rounded-md transition-colors"
                      >
                        <div className="w-6 h-6 rounded-full bg-[#1a1d24] border border-white/10 flex items-center justify-center group-hover:border-blue-500/50 transition-colors">
                          <User size={12} className="text-zinc-500 group-hover:text-blue-400 transition-colors" />
                        </div>
                        <div>
                          <p className="text-sm text-zinc-300 group-hover:text-blue-400 transition-colors">{org.primaryContact}</p>
                          {org.primaryContactRole && (
                            <p className="text-xs text-zinc-500">{org.primaryContactRole}</p>
                          )}
                        </div>
                      </button>
                    ) : (
                      <span className="text-zinc-600 text-xs">Not set</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {org.primaryContactEmail || org.primaryContactPhone ? (
                      <div className="space-y-1">
                        {org.primaryContactEmail && (
                          <a
                            href={`mailto:${org.primaryContactEmail}`}
                            className="flex items-center gap-2 text-zinc-400 hover:text-blue-400 text-xs transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Mail size={12} />
                            <span className="truncate max-w-[180px]">{org.primaryContactEmail}</span>
                          </a>
                        )}
                        {org.primaryContactPhone && (
                          <a
                            href={`tel:${org.primaryContactPhone}`}
                            className="flex items-center gap-2 text-zinc-500 hover:text-blue-400 text-xs transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone size={12} />
                            <span>{org.primaryContactPhone}</span>
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="text-zinc-600 text-xs">Not set</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    {org.location ? (
                      <div className="flex items-center gap-2 text-zinc-400 text-sm">
                        <MapPin size={14} />
                        <span>{org.location}</span>
                      </div>
                    ) : (
                      <span className="text-zinc-600 text-xs">Not set</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    {org.aum ? (
                      <div className="flex items-center gap-2 text-zinc-300 text-sm">
                        <DollarSign size={14} className="text-zinc-500" />
                        <span>{formatCurrency(org.aum)}</span>
                      </div>
                    ) : (
                      <span className="text-zinc-600 text-xs">Not set</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <div className="flex items-center gap-1 overflow-hidden">
                      {org.tags && org.tags.length > 0 ? (
                        <>
                          {org.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border whitespace-nowrap flex-shrink-0"
                              style={{
                                backgroundColor: `${tag.color}15`,
                                color: tag.color,
                                borderColor: `${tag.color}30`
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                          {org.tags.length > 2 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-zinc-700/50 text-zinc-400 border border-zinc-600/50 whitespace-nowrap flex-shrink-0">
                              +{org.tags.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-zinc-600 text-xs">No tags</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <button
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                          handleClearLastContact(org.id);
                        } else {
                          handleUpdateLastContact(org.id);
                        }
                      }}
                      className="flex items-center gap-2 text-zinc-500 text-xs hover:text-blue-400 hover:bg-white/5 rounded px-3 py-2 -mx-3 -my-2 transition-colors group w-full min-w-[180px]"
                      title="Click to update to now • Cmd/Ctrl+Click to clear"
                    >
                      <span className="flex-grow">
                        {org.lastContact 
                          ? new Date(org.lastContact).toLocaleString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }) 
                          : 'Set date'}
                      </span>
                      <Clock size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400 flex-shrink-0" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-zinc-200 hover:bg-white/5">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#0f1115] border-white/10">
                        <DropdownMenuItem 
                          className="text-zinc-300 focus:bg-white/5 focus:text-white cursor-pointer"
                          onClick={() => handleViewOrganization(org)}
                        >
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-zinc-300 focus:bg-white/5 focus:text-white cursor-pointer"
                          onClick={() => handleEditOrganization(org)}
                        >
                          Edit Organization
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-zinc-300 focus:bg-white/5 focus:text-white cursor-pointer"
                          onClick={() => org.primaryContactEmail && (window.location.href = `mailto:${org.primaryContactEmail}`)}
                        >
                          Send Email
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                          onClick={() => handleDeleteOrganization(org.id)}
                        >
                          Delete Organization
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Info */}
      <div className="flex items-center justify-between text-sm text-zinc-500">
        <span>
          Showing {filteredOrgs.length} of {organizations.length} organizations
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled
            className="bg-transparent border-white/10 text-zinc-500 disabled:opacity-50"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled
            className="bg-transparent border-white/10 text-zinc-500 disabled:opacity-50"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Create/Edit Organization Modal */}
      <CreateOrganizationModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingOrganization(null);
        }}
        onSubmit={handleCreateOrganization}
        editOrganization={editingOrganization}
      />

      {/* View Organization Modal */}
      <ViewOrganizationModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingOrganization(null);
        }}
        organization={viewingOrganization}
        onEdit={handleEditOrganization}
      />
    </motion.div>
  );
};

export default Organizations;
